import _ from 'lodash';

import { IChartData, IVideo } from './interfaces';

interface IFullChartData extends IChartData {
    yAxis: Array<string>;
}

interface IProbabilityHover {
    playerName: string;
    position: string;
    probability: number;
    round: string;
    thru?: string;
    toPar: string;
}

interface IProbabilityPoint {
    holeNumber: number;
    value?: number;
    hover?: IProbabilityHover;
    video?: IVideo;
}

interface IProbabilityRound {
    roundNumber: number;
    playerPoints: Array<IProbabilityPoint>;
    secondHighestPoints?: Array<IProbabilityPoint>;
    playerPreEstimatedPoint?: IProbabilityPoint;
}

export const PRE_LABEL = 'pre';
const Y_AXIS_COUNT = 4;

const defaultChartData = {
    points: {
        playerPoints: [],
    },
    yMax: 0,
    xAxis: [],
    videos: {},
};

const getHoles = (points: Array<IProbabilityPoint>): Array<string> => {
    const holes = points.map((p) => p.holeNumber.toString());

    return [PRE_LABEL, ...holes];
};

const getGraphPoints = (
    points: Array<IProbabilityPoint>,
    preProbability?: IProbabilityPoint,
): Array<number | null> => {
    const array = points.map((p) => {
        // Use null as fallback to avoid chart redraw with undefined array items
        return p.value === undefined ? null : p.value;
    });

    if (preProbability && !_.isNil(preProbability.value)) {
        return [preProbability.value, ...array];
    }

    return [null, ...array];
};

const getMaxPercent = (value: number) => {
    const prc = value * 100;
    let maxPrc = 4;

    while (maxPrc < prc) {
        maxPrc += 4;
    }

    return maxPrc;
};

const getYAxis = (maxPercent: number): Array<string> => {
    const tick = Math.floor(maxPercent / 4);
    const result: Array<string> = [];

    let prc = maxPercent;
    let ticks = Y_AXIS_COUNT;

    while (ticks > 0) {
        result.push(`${prc}%`);

        prc -= tick;
        ticks--;
    }

    result.push('0%');

    return result;
};

const getVideos = (
    points: Array<IProbabilityPoint>,
): Record<string, IVideo> => {
    return points.reduce<Record<string, IVideo>>((memo, item) => {
        const video = item.video;

        if (!video) {
            return memo;
        }

        return {
            ...memo,
            [item.holeNumber]: video,
        };
    }, {});
};

const getPointsHover = (
    playerPoints: Array<IProbabilityPoint>,
    prePoint?: IProbabilityPoint,
): Array<IProbabilityHover | null> => {
    const result: Array<IProbabilityHover | null> = [];

    if (prePoint) {
        result.push(prePoint.hover || null);
    } else {
        result.push(null);
    }

    playerPoints.forEach((point) => {
        if (point.hover) {
            result.push(point.hover);
        }
    });

    return result;
};

export function prepareChartData(
    rounds: Array<IProbabilityRound>,
    secondPlayerRounds?: Array<IProbabilityRound>,
    intend = true,
): Array<IFullChartData> {
    const chartData = rounds.reduce<Array<IChartData>>((memo, round, index) => {
        if (!round.playerPoints) {
            return [...memo, defaultChartData];
        }

        const playerPoints = getGraphPoints(
            round.playerPoints,
            round.playerPreEstimatedPoint,
        );
        const secondPlayerRound = _.get(secondPlayerRounds, index);
        const secondPlayerPoints = secondPlayerRound
            ? getGraphPoints(
                  secondPlayerRound.playerPoints,
                  secondPlayerRound.playerPreEstimatedPoint,
              )
            : undefined;
        const allPoints = secondPlayerPoints
            ? [...playerPoints, ...secondPlayerPoints]
            : playerPoints;
        const yMax = _.max(allPoints) || 0;

        let result = {
            points: {
                playerPoints,
                secondPlayerPoints,
                playerPointsHover: getPointsHover(
                    round.playerPoints,
                    round.playerPreEstimatedPoint,
                ),
                secondPlayerPointsHover: secondPlayerRound
                    ? getPointsHover(
                          secondPlayerRound.playerPoints,
                          secondPlayerRound.playerPreEstimatedPoint,
                      )
                    : [],
            },
            yMax,
            xAxis: getHoles(round.playerPoints),
            videos: getVideos(round.playerPoints),
        };

        return [...memo, result];
    }, []);

    const yMaxAll = chartData.reduce(
        (memo, item) => (item.yMax > memo ? item.yMax : memo),
        0,
    );
    const maxPrc = getMaxPercent(yMaxAll);
    const yAxis = getYAxis(maxPrc);
    const prc = intend ? maxPrc * 1.25 : maxPrc * 1.02;

    return chartData.map((data) => {
        return {
            ...data,
            yMax: prc / 100,
            yAxis,
        };
    });
}
